import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import {
  Form,
  Input,
  Button,
  Row,
  Col,
  Spin,
  Modal,
  message,
  DatePicker,
  Tag,
} from 'antd';
import { onError } from 'utils/handlers';
import Table from 'components/Table';
import FormSubmitControls from 'components/FormSubmitControls';
import ModalDelete from 'components/ModalDelete';
import Select from 'components/Select';
import createClave from 'utils/keyGenerator';
import { isMutable, isAuthorized } from 'utils/estadosGlobales';
import getFormattedValues, { DATE_FRONTEND_FORMAT } from 'utils/formatValues';
import {
  deleteHistorialDePlacas,
  getHistorialDePlacas,
  patchHistorialDePlacas,
  postHistorialDePlacas,
} from 'api/recaudacion/historialDePlacas';
import {
  CloseCircleOutlined,
  EllipsisOutlined,
  InfoCircleOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { noSymbolsCapitalized } from 'utils/normalizers';
import { InputSearchPro } from 'components/InputSearch';
import { getCiudadanoCaja } from 'api/cuentaunicasir/index';
import { getCiudadano } from 'api/cuentaunicasir';
import { getContribuyente, getContribuyenteCaja } from 'api/empresas/contribuyentes';
import { getEstatusDePlacas } from 'api/catalogos';
import { patchVehiculo } from 'api/recaudacion/vehiculos';

export const permissionConfig = {
  title: 'Historial de Placas',
  model: 'placavehicular',
};

const HistorialDePlacas = ({
  vehiculoId,
  permission,
  esPropietarioCiudadano,
  ciudadanoPropietarioId,
  contribuyentePropietarioId,
  setCurrentTabKey,
  justView,
  callback,
}) => {
  const estadosGlobales = useSelector(({ catalogs }) => catalogs.estadosGlobales);
  const [form] = Form.useForm();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [visible, setVisible] = useState(false);
  const [visibleAlert, setVisibleAlert] = useState(false);
  const [data, setData] = useState([]);
  const [mutable, setMutable] = useState(true);
  const [ciudadanoFound, setCiudadanoFound] = useState([]);
  const [contribuyenteFound, setContribuyenteFound] = useState([]);
  const [estatusPlacas, setEstatusPlacas] = useState([]);
  const [selectedEstado, setSelectedEstado] = useState(1);
  const [isEditing, setIsEditing] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    const _data = await getHistorialDePlacas(vehiculoId);
    setData(_data);
    setLoading(false);
  };

  useEffect(() => {
    let mounted = true;
    const _fetchData = async () => {
      setLoading(true);
      const _data = await getHistorialDePlacas(vehiculoId);
      const _estadosPlacas = await getEstatusDePlacas();
      if (mounted) {
        setData(_data);
        setEstatusPlacas(_estadosPlacas);
        setLoading(false);
      }
    };
    if (vehiculoId) {
      _fetchData();
    }
    return () => { mounted = false; };
    // eslint-disable-next-line
  }, []);

  const searchCiudadano = async () => {
    try {
      setLoading(true);
      await form.validateFields(['q']);
      const { q } = form.getFieldsValue(['q']);
      if (q) {
        const _ciudadanos = await getCiudadanoCaja({ q });
        if ([1].includes(_ciudadanos.results.length)) {
          const [selected] = _ciudadanos.results;
          form.setFieldsValue({
            ciudadano: selected.id,
          });
          setCiudadanoFound([selected]);
        } if (!_ciudadanos.results.length) {
          message.info('No se encontró ciudadano');
        }
      } else {
        await form.validateFields(['q']);
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const searchContribuyente = async () => {
    try {
      setLoading(true);
      await form.validateFields(['q']);
      const { q } = form.getFieldsValue(['q']);
      if (q) {
        const _contribuyente = await getContribuyenteCaja({ q });
        if (_contribuyente?.results?.length === 1) {
          const [selected] = _contribuyente.results;
          form.setFieldsValue({
            contribuyente_propietario: selected.id,
          });
          setContribuyenteFound([selected]);
        } if (!_contribuyente.results.length) {
          message.info('No se encontró Contribuyente');
        }
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const fetchCiudadano = async (id) => {
    setLoading(true);
    if (id) {
      const _ciudadano = await getCiudadano(id);

      if (_ciudadano) {
        form.setFieldsValue({
          ciudadano: _ciudadano?.id,
        });
        setCiudadanoFound([_ciudadano]);
      } else {
        message.info('No se encontró ciudadano');
      }
    }
    setLoading(false);
  };

  const fetchContribuyente = async (id) => {
    setLoading(true);
    if (id) {
      const _contribuyente = await getContribuyente(id);
      if (_contribuyente) {
        form.setFieldsValue({
          contribuyente_propietario: _contribuyente?.id,
        });
        setContribuyenteFound([_contribuyente]);
      } else {
        message.info('No se encontró el Contribuyente');
      }
    }
    setLoading(false);
  };

  const onCancel = () => {
    setVisible(false);
    form.resetFields();
    setSelectedRowKeys([]);
    setMutable(true);
    setIsEditing(false);
  };

  const content = (
    <>
      <Row justify="end">
        <Button
          type="link"
          style={{ color: '#000000', marginTop: -25 }}
          icon={<CloseCircleOutlined />}
          onClick={() => message.destroy()}
        />
      </Row>
      <strong>Hace falta buscar un propietario.</strong>
      <br />
      <span>
        Para buscar un propietario ubiquese en la pestaña
        {' '}
        <Tag
          icon={<InfoCircleOutlined style={{ color: '#000000', margin: 0 }} />}
          onClick={() => {
            setCurrentTabKey('info');
            setVisible(false);
          }}
        >
          Información del Vehículo
        </Tag>
        seleccione a que padrón pertenece (Contribuyente o Ciudadano) y en el
        campo ubicado
        a la derecha introduzca algún dato del padrón correspondiente y presione el icono
        de búsqueda (
        <SearchOutlined style={{ color: '#000000', margin: 0 }} />
        ), en caso de que no se arroje alguna coincidencia presione el botón (
        <EllipsisOutlined style={{ color: '#000000', margin: 0 }} />
        ) para realizar una búsqueda avanzada.
      </span>
    </>
  );

  const contentPlaca = (
    <>
      <Row justify="end">
        <Button
          type="link"
          style={{ color: '#000000', marginTop: -25 }}
          icon={<CloseCircleOutlined />}
          onClick={() => message.destroy()}
        />
      </Row>
      <strong>No se puede agregar una placa nueva si ya hay una vigente.</strong>
      <br />
      Si en el historial existe una placa vigente, primero manualmente aplica la baja de esta misma,
      posteriormente podras agregar una nueva placa vigente y asignarla al vehiculo actual
    </>
  );

  const isBajaManual = (isEditing && selectedEstado === 3);

  const onFinish = async () => {
    try {
      setLoading(true);
      await form.validateFields();
      const values = form.getFieldsValue();
      const formattedValues = getFormattedValues(values);
      const valuesPatch = {
        numero_de_placa_vigente: formattedValues.numero_de_placa,
      };
      if (isBajaManual) {
        valuesPatch.dar_baja_placa_por_reemplacamiento = true;
        valuesPatch.numero_de_placa_vigente = null;
      }
      if ((esPropietarioCiudadano && !values?.ciudadano)
      || (!esPropietarioCiudadano && !values?.contribuyente_propietario)) {
        message.info({ content, duration: 0 });
      } else {
        const [key] = selectedRowKeys;
        const _saved = await (key
          ? patchHistorialDePlacas(vehiculoId, formattedValues)
          : postHistorialDePlacas(vehiculoId, formattedValues));
        if (_saved) {
          const baja = await patchVehiculo(
            vehiculoId,
            valuesPatch,
            false,
          );
          if (baja) {
            onCancel();
            callback();
          }
        }
        await fetchData();
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const deleteItem = async () => {
    setLoading(true);
    const [key] = selectedRowKeys;
    const _deleted = await deleteHistorialDePlacas(key, vehiculoId);
    if (_deleted) {
      setVisibleAlert(false);
      onCancel();
      await fetchData();
    }
    setLoading(false);
  };

  const handleOnRowClick = (record) => {
    setSelectedRowKeys([record.id]);
    setMutable(isMutable(record));
    form.setFieldsValue(record);
  };

  const rowSelection = {
    selectedRowKeys,
    type: 'radio',
  };
  const resetAndValidate = () => {
    setContribuyenteFound([]);
    setCiudadanoFound([]);
    form.resetFields(['q']);
    form.validateFields(['q']);
  };

  const onClickAdd = () => {
    const hasCurrentPlaca = data?.find((e) => e.vigente);
    if (hasCurrentPlaca) {
      message.info({ content: contentPlaca, duration: 0 });
    } else {
      onCancel();
      createClave(data, form);
      if (esPropietarioCiudadano) {
        fetchCiudadano(ciudadanoPropietarioId);
      } else {
        fetchContribuyente(contribuyentePropietarioId);
      }
      setVisible(true);
    }
  };

  const onClickEdit = async () => {
    setLoading(true);
    if (esPropietarioCiudadano) {
      await fetchCiudadano(ciudadanoPropietarioId);
    } else {
      await fetchContribuyente(contribuyentePropietarioId);
    }
    setLoading(false);
    setVisible(true);
    setIsEditing(true);
  };

  const onClickDelete = () => {
    const [key] = selectedRowKeys;
    const match = data.find((e) => e.id === key);
    if (isMutable(match)) {
      setVisibleAlert(true);
    } else {
      message.warn(`No se puede eliminar un registro ${isAuthorized(match) ? 'autorizado' : 'cancelado'}`);
    }
  };

  const rules = {
    required: [{ required: true }],
  };

  const columns = [
    {
      titleText: 'Placas',
      dataIndex: 'numero_de_placa',
      key: 'numero_de_placa',
      width: 200,
    },
    {
      titleText: 'Vigente',
      dataIndex: 'vigente',
      key: 'vigente',
      width: 200,
      render: (val) => (val ? 'Si' : 'No'),
    },
    {
      titleText: 'Fecha de Baja',
      dataIndex: 'fecha_de_baja',
      key: 'fecha_de_baja',
      width: 200,
      render: (val) => (val?.format(DATE_FRONTEND_FORMAT)),
    },
    {
      titleText: 'Estado',
      dataIndex: 'estados_globales',
      key: 'estados_globales',
      width: 100,
      render: (e) => estadosGlobales.find((i) => i.id === e)?.descripcion,
    },
  ];

  return (
    <Row align="center" justify="center" className="container">
      <Spin tip="Cargando..." spinning={loading}>
        <Table
          cols={columns}
          data={data}
          rowSelection={rowSelection}
          handleOnRowClick={handleOnRowClick}
          controls={{
            onClickAdd,
            onClickEdit,
            onClickDelete,
          }}
          mobileColIndex={0}
          permission={permission}
        />
        <Modal
          open={visible}
          closable={false}
          footer={null}
          maskClosable={!loading}
          keyboard={!loading}
          title={(
            <FormSubmitControls
              label={`${selectedRowKeys.length ? 'Editar' : 'Agregar'} placas`}
              onCancel={onCancel}
              onFinish={onFinish}
              mutable={mutable}
              permission={permission}
            />
          )}
          onCancel={onCancel}
          forceRender
          centered
        >
          <Form
            layout="vertical"
            form={form}
            onFinish={() => onFinish()}
            scrollToFirstError
            initialValues={{
              estados_globales: 1,
              ciudadano: ciudadanoPropietarioId,
              contribuyente_propietario: contribuyentePropietarioId,
              estado_de_la_placa: isEditing ? 3 : 1,
              entidad: 1,
              cantidad_de_placas: 2,
              estado: 18,
            }}
          >
            <Row gutter={10}>
              <Col span={12}>
                <Form.Item
                  name="numero_de_placa"
                  rules={rules.required}
                  label="Placas"
                  hasFeedback
                  normalize={noSymbolsCapitalized}

                >
                  <Input maxLength={10} allowClear />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="vigente"
                  rules={rules.required}
                  label="Vigente"
                  hasFeedback
                >
                  <Select trueFalse />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="folio_de_tramite"
                  label="Folio de Trámite"
                >
                  <Input trueFalse />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="folio_de_recibo"
                  label="Folio de Recibo"
                >
                  <Input trueFalse />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="folio_de_recibo_alta"
                  label="Folio de Recibo de Alta"
                >
                  <Input trueFalse />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="fecha_expedicion_placa"
                  label="Fecha de Expedición"
                >
                  <DatePicker
                    disabledDate={(date) => date.isAfter(new Date())}
                    format={DATE_FRONTEND_FORMAT}
                    disabled={justView}
                  />
                </Form.Item>
              </Col>
              {esPropietarioCiudadano ? (
                <InputSearchPro
                  disabled={!mutable || justView}
                  tooltip="Búsqueda por correo"
                  label="Ciudadano Propietario"
                  name="q"
                  rules={rules.required}
                  breakPoints={{
                    xs: 24,
                    sm: 24,
                    md: 24,
                  }}
                  inputProps={{
                    onPressEnter: () => searchCiudadano(),
                    onChange: ({ target: { value } }) => {
                      if (!value) {
                        resetAndValidate();
                      }
                    },
                  }}
                  resultProps={{
                    name: 'ciudadano',
                    label: 'Ciudadano Propietario',
                    dataSource: ciudadanoFound,
                    labelProp: 'nombre_completo',
                    onClear: resetAndValidate,
                    breakPoints: {
                      xs: 24,
                      sm: 24,
                      md: 24,
                    },
                  }}
                />
              ) : (
                <InputSearchPro
                  disabled={!mutable || justView}
                  tooltip="Búsqueda por Nombre Completo"
                  label="Contribuyente Propietario"
                  name="q"
                  rules={rules.required}
                  breakPoints={{
                    xs: 24,
                    sm: 24,
                    md: 24,
                  }}
                  inputProps={{
                    onPressEnter: () => searchContribuyente(),
                    onChange: ({ target: { value } }) => {
                      if (!value) {
                        resetAndValidate();
                      }
                    },
                  }}
                  resultProps={{
                    breakPoints: {
                      xs: 24,
                      sm: 24,
                      md: 24,
                    },
                    name: 'contribuyente_propietario',
                    label: 'Contribuyente Propietario',
                    dataSource: contribuyenteFound,
                    labelProp: contribuyenteFound[0]?.nombre_completo ? 'nombre_completo' : 'razon_social',
                    onClear: resetAndValidate,
                  }}
                />
              )}
              <Col span={24}>
                <Form.Item
                  name="estado_de_la_placa"
                  label="Estatus de Placa"
                >
                  <Select
                    onChange={setSelectedEstado}
                    dataSource={estatusPlacas}
                    disabled={!isEditing || justView}
                  />
                </Form.Item>
              </Col>
              {selectedEstado === 3 && (
              <Col span={24}>
                <Form.Item
                  name="fecha_de_baja"
                  label="Fecha de Baja"
                >
                  <DatePicker placeholder="" format="DD/MM/YYYY" />
                </Form.Item>
              </Col>
              )}
              {(selectedEstado === 2 || selectedEstado === 3) && (
              <>
                <Col span={24}>
                  <Form.Item
                    name="razon_de_suspencion"
                    label={`Razón de ${selectedEstado === 2 ? 'suspensión' : 'baja'}`}
                  >
                    <Input allowClear />
                  </Form.Item>
                </Col>
                <Col span={24}>
                  <Form.Item
                    name="folio_de_documento_de_denuncia"
                    label="Folio de Documento de Denuncia"
                  >
                    <Input allowClear />
                  </Form.Item>
                </Col>
              </>
              )}
              <Col span={24}>
                <Form.Item
                  name="estados_globales"
                  rules={rules.required}
                  label="Estado"
                  hasFeedback
                >
                  <Select dataSource={estadosGlobales} disabled={!selectedRowKeys.length} />
                </Form.Item>
              </Col>
              <Form.Item name="id" hidden>
                <Input />
              </Form.Item>
              <Form.Item hidden>
                <Button htmlType="submit" />
              </Form.Item>
            </Row>
          </Form>
        </Modal>
        <ModalDelete
          onDelete={deleteItem}
          onCancel={() => {
            setVisibleAlert(false);
            setSelectedRowKeys([]);
          }}
          visible={visibleAlert}
          content="el registro de placas"
          loading={loading}
        />
      </Spin>
    </Row>
  );
};

HistorialDePlacas.propTypes = {
  vehiculoId: PropTypes.number,
  permission: PropTypes.shape({
    add: PropTypes.bool.isRequired,
    authorize: PropTypes.bool.isRequired,
    cancel: PropTypes.bool.isRequired,
    change: PropTypes.bool.isRequired,
    delete: PropTypes.bool.isRequired,
    export: PropTypes.bool.isRequired,
    import: PropTypes.bool.isRequired,
    sync: PropTypes.bool.isRequired,
    view: PropTypes.bool.isRequired,
  }).isRequired,
  esPropietarioCiudadano: PropTypes.bool,
  contribuyentePropietarioId: PropTypes.number,
  ciudadanoPropietarioId: PropTypes.number,
  setCurrentTabKey: PropTypes.func.isRequired,
  justView: PropTypes.bool,
  callback: PropTypes.func.isRequired,
};

HistorialDePlacas.defaultProps = {
  vehiculoId: null,
  contribuyentePropietarioId: null,
  ciudadanoPropietarioId: null,
  esPropietarioCiudadano: false,
  justView: false,
};

export default HistorialDePlacas;
